import React, { useState, useRef, useEffect } from "react";
import { AllIcons } from "./IconsList";

//font awsome icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExpand } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlassPlus } from "@fortawesome/free-solid-svg-icons";
import { faMagnifyingGlassMinus } from "@fortawesome/free-solid-svg-icons";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const Image = ({ src, alt = "Image", fileName, parentFolder }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageNaturalSize, setImageNaturalSize] = useState({
    width: 0,
    height: 0,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [currentFileName, setCurrentFileName] = useState(fileName);
  let [currentIndex, setCurrentIndex] = useState(0);

  const handleChange = (direction) => {
    const folderImages = AllIcons.filter(
      (icon) => icon.file_type === "i" && icon.parentFolder === parentFolder,
    );
    for (let i = 0; i < folderImages.length; i++) {
      if (currentFileName === folderImages[i].file_name) {
        currentIndex = i;
        setCurrentIndex(i);
        break;
      }
    }

    let nextIndex = currentIndex;
    if (direction === "r") {
      nextIndex = currentIndex + 1;
    } else {
      nextIndex = currentIndex - 1;
      if (nextIndex < 0) {
        nextIndex = folderImages.length - 1;
      }
    }
    nextIndex = nextIndex % folderImages.length;
    setImageLoaded(false);
    setCurrentSrc(folderImages[nextIndex].src);
    setCurrentFileName(folderImages[nextIndex].file_name);
    setCurrentIndex(nextIndex);
  };

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Reset position when scale changes
  useEffect(() => {
    if (scale <= 1) {
      setPosition({ x: 0, y: 0 });
    }
  }, [scale]);

  // Handle wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.005;
    const newScale = Math.min(Math.max(0.1, scale + delta), 5);
    setScale(newScale);
  };

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (scale <= 1) return; // Only allow dragging when zoomed in

    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;

    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    };

    // Constrain movement to prevent dragging image too far
    const container = containerRef.current;
    const image = imageRef.current;

    if (container && image) {
      const containerRect = container.getBoundingClientRect();
      const imageRect = image.getBoundingClientRect();

      const maxX = Math.max(0, (imageRect.width - containerRect.width) / 2);
      const maxY = Math.max(0, (imageRect.height - containerRect.height) / 2);

      newPosition.x = Math.min(maxX, Math.max(-maxX, newPosition.x));
      newPosition.y = Math.min(maxY, Math.max(-maxY, newPosition.y));
    }

    setPosition(newPosition);
  };

  // Handle mouse up
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart, position, scale]);

  // Zoom functions
  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 5));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.1));
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const fitToScreen = () => {
    if (
      !imageNaturalSize.width ||
      !imageNaturalSize.height ||
      !containerRef.current
    )
      return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth - 40; // Account for padding
    const containerHeight = container.clientHeight - 80; // Account for toolbar and padding

    const scaleX = containerWidth / imageNaturalSize.width;
    const scaleY = containerHeight / imageNaturalSize.height;
    const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 100%

    setScale(newScale);
    setPosition({ x: 0, y: 0 });
  };

  // Handle image load
  const handleImageLoad = (e) => {
    setImageLoaded(true);
    setImageError(false);
    setImageNaturalSize({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
    });
  };

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle escape key for fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  const toolbarStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "40px",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 10px",
    zIndex: 10,
    color: "white",
    fontSize: "12px",
  };

  const buttonStyle = {
    background: "rgba(255, 255, 255, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "3px",
    color: "white",
    padding: "4px 8px",
    margin: "0 2px",
    cursor: "pointer",
    fontSize: "11px",
    transition: "all 0.2s ease",
  };

  const containerStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    backgroundColor: "#2a2a2a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
  };

  const imageContainerStyle = {
    position: "relative",
    width: "100%",
    height: "calc(100% - 40px)",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
  };

  const imageStyle = {
    maxWidth: scale <= 1 ? "100%" : "none",
    maxHeight: scale <= 1 ? "100%" : "none",
    transform: `scale(${scale}) translate(${position.x / scale}px, ${
      position.y / scale
    }px)`,
    transformOrigin: "center center",
    transition: isDragging ? "none" : "transform 0.2s ease",
    display: "block",
  };

  const fullscreenStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.95)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  if (isFullscreen) {
    return (
      <div style={fullscreenStyle} onClick={toggleFullscreen}>
        <img
          src={currentSrc}
          alt={alt}
          style={{
            maxWidth: "95%",
            maxHeight: "95%",
            objectFit: "contain",
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            color: "white",
            fontSize: "24px",
            cursor: "pointer",
            background: "rgba(0, 0, 0, 0.5)",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          ×
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} ref={containerRef}>
      {/* Toolbar */}
      <div style={toolbarStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <span>{currentFileName}</span>
          {imageLoaded && (
            <span style={{ opacity: 0.7 }}>
              ({imageNaturalSize.width} × {imageNaturalSize.height}) -{" "}
              {Math.round(scale * 100)}%
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            style={buttonStyle}
            onClick={zoomOut}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Zoom Out"
          >
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} />
          </button>
          <button
            style={buttonStyle}
            onClick={resetZoom}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Reset Zoom (100%)"
          >
            1:1
          </button>
          <button
            style={buttonStyle}
            onClick={zoomIn}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Zoom In"
          >
            <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
          </button>
          <button
            style={buttonStyle}
            onClick={fitToScreen}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Fit to Screen"
          >
            Fit
          </button>
          <button
            style={buttonStyle}
            onClick={() => handleChange("l")}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Previous Image"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <button
            style={buttonStyle}
            onClick={() => handleChange("r")}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Next Image"
          >
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
          <button
            style={buttonStyle}
            onClick={toggleFullscreen}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")
            }
            title="Fullscreen"
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
        </div>
      </div>

      {/* Image Container */}
      <div
        style={imageContainerStyle}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
      >
        {!imageLoaded && !imageError && (
          <div style={{ color: "white", textAlign: "center" }}>
            <div style={{ fontSize: "24px", marginBottom: "10px" }}>
              <FontAwesomeIcon icon={faCamera} />
            </div>
            <div>Loading image...</div>
          </div>
        )}

        {imageError && (
          <div style={{ color: "white", textAlign: "center" }}>
            <div style={{ fontSize: "24px", marginBottom: "10px" }}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <div>Failed to load image</div>
            <div style={{ fontSize: "11px", opacity: 0.7, marginTop: "5px" }}>
              {src}
            </div>
          </div>
        )}

        <img
          ref={imageRef}
          src={currentSrc}
          alt={alt}
          style={{
            ...imageStyle,
            display: imageLoaded ? "block" : "none",
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      {imageLoaded && scale <= 1 && (
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255, 255, 255, 0.6)",
            fontSize: "11px",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          Scroll to zoom • Click and drag when zoomed
        </div>
      )}
    </div>
  );
};

export default Image;
